import { inject, Injectable } from '@angular/core';
import { EncryptionService, PREFIX_ENCRYPTED_CONTENT, PREFIX_ENCRYPTED_CONTENT_LEGACY } from '@evorait/pace-encryption';
import CryptoJS from 'crypto-js';
import { decompressFromUint8Array } from 'lz-string';

/**
 *
 * This class is only relevant for the transition period from GW encryption
 * keys to Azure encryption keys. Once, all users that have on encryption
 * key on GW also have an encryption key on Azure, it can safely be removed.
 * ~ summer 2026 should be safe
 */
@Injectable({
    providedIn: 'root',
})
export class EncryptionMigrator {
    private readonly encryptionService = inject(EncryptionService);

    public static readonly PREFIX_LEGACY_ENCRYPTED_KEY = '[[-AESENCKEY-]]';
    private static readonly MIGRATION_DONE_MARKER = 'ENC_MIGRATED';
    private static readonly MIGRATION_IN_PROCESS_MARKER = 'ENC_MIGRATION';

    private oldKey?: string = undefined;

    public isMigrated(): boolean {
        return !!localStorage.getItem(EncryptionMigrator.MIGRATION_DONE_MARKER);
    }

    public noteMigrationStarted(): void {
        const previousAttempts = localStorage.getItem(EncryptionMigrator.MIGRATION_IN_PROCESS_MARKER);
        const prefix = previousAttempts ? `${previousAttempts}, ` : '';
        localStorage.setItem(EncryptionMigrator.MIGRATION_IN_PROCESS_MARKER, prefix + new Date().toISOString());
    }

    public setMigrated(): unknown {
        return localStorage.setItem(EncryptionMigrator.MIGRATION_DONE_MARKER, new Date().toISOString());
    }

    public setLegacyKey(key: string): void {
        this.oldKey = key;
    }

    public async reEncrypt(data: string): Promise<string> {
        const decrypted = await this.decryptWithOldKey(data);
        return this.encryptionService.encryptAsync(decrypted);
    }

    public async reEncryptCompressed(data: Uint8Array): Promise<Uint8Array> {
        const decompressed = decompressFromUint8Array(data);
        const decrypted = await this.decryptWithOldKey(decompressed);
        return this.encryptionService.encryptAndCompressAsync(decrypted);
    }

    private async decryptWithOldKey(data: string): Promise<string> {
        if (!this.oldKey) {
            throw new Error(
                `EncryptionMigratorService: decrypt: Trying to call EncryptionService before initialization!`,
            );
        }

        if (!data || data.length < 1) {
            console.error(
                `EncryptionMigratorService: decrypt: The provided data was empty. Empty data cannot be decrypted.`,
            );
            throw new Error(
                `EncryptionMigratorService: decrypt: The provided data was empty. Empty data cannot be decrypted.`,
            );
        }

        if (data.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY)) {
            const decrypted = data.replace(PREFIX_ENCRYPTED_CONTENT_LEGACY, '');
            return CryptoJS.AES.decrypt(decrypted, this.oldKey).toString(CryptoJS.enc.Utf8);
        }

        if (data.startsWith(PREFIX_ENCRYPTED_CONTENT)) {
            return this.encryptionService.decryptAsync(data);
        }

        return data;
    }
}
